import cssReset from '@atlaskit/css-reset/styles';
import { token } from '@atlaskit/tokens';
import { useStatsig } from '@atlassian/mpac-feature-flags';
import React, { useEffect, useRef } from 'react';

import type { ReactNode } from 'react';

import AtlassianLogoFullWhite from '../assets/images/AtlassianLogoFullWhite.svg';

/**
 * Includes
 * - Atlaskit CSS Reset to provide extremely basic styling
 * - Some styles from html, body
 * - Misc global styles
 */
const getGlobalStyles = () => `
  ${cssReset}

  html,
  body.app {
    width: 100%;
    margin: 0;
    padding: 0;
    overflow-x: hidden;
  }

  body.app {
    background: ${token('elevation.surface')};
    display: flex;
    flex-direction: column;
    min-height: 100vh;
    box-sizing: border-box;
  }

  body.app header[role='banner'] {
    margin-bottom: 0;
    min-width: 0;
  }

  body.app section[role='main'] {
    flex: 1 0 auto;
  }

  *,
  *:before,
  *:after {
    box-sizing: inherit;
  }

  /** Bootstrap data from the server. These elements are removed from the DOM on page load. */
  .data[data-id] {
    display: none;
  }
`;

/**
 * Styles for the site footer which contains Atlassian logo & few static links.
 * These are part of the inital static HTML and hence is styled with global styles.
 */
const getSiteFooterStyles = () => `
  footer[role='contentinfo'] {
    background-color: ${token('elevation.surface')};
    flex: 0 0 auto;
    padding-top: ${token('space.250')};
    padding-bottom: ${token('space.250')};
    width: 100%;
    color: ${token('color.text.subtlest')};
  }

  footer[role='contentinfo'] a {
    color: ${token('color.text.subtlest')};
  }

  footer[role='contentinfo'] .logo-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  footer[role='contentinfo'] .bottom-logo {
    background: url('${AtlassianLogoFullWhite}') no-repeat;
    background-repeat: no-repeat;
    margin-left: 30px;
    width: 160px;
    height: 20px;
  }

  footer[role='contentinfo'] section {
    height: 40px;
  }

  footer[role='contentinfo'] section nav {
    float: right;
  }

  footer[role='contentinfo'] section nav ul li {
    list-style: none;
    display: inline-block;
    white-space: nowrap;
    margin: 0 30px 0 0;
  }
`;

export const GlobalStyles = ({ children }: { children?: ReactNode }) => {
  const enableWCFonts = useRef(false);
  const { enableMpacWebCoherence } = useStatsig();

  useEffect(() => {
    if (enableMpacWebCoherence() && !enableWCFonts.current) {
      // We have to override brand font for body to use new Atlassian sans font
      // when Web Coherence is enabled, to make the change backward compatible.
      document.documentElement.style.setProperty(
        '--ds-font-family-brand-body',
        token('font.family.body')
      );
      enableWCFonts.current = true;
    }
  }, [enableMpacWebCoherence]);

  return (
    <>
      <style data-global-styles="true" data-testid="global-styles">
        {getGlobalStyles() + getSiteFooterStyles()}
      </style>
      {children}
    </>
  );
};
